/** @file Projects browser toolbar: search, sort, trash view switch and the new-project form. */
import type { CatalogKey, Translate } from '@pose2d/i18n/types';
import type { SubmitEvent } from 'react';
import { formField } from '../../shell/formField';
import type { ProjectFilter } from './filterProjects';

const SORTS: { id: ProjectFilter['sort']; label: CatalogKey }[] = [
  { id: 'updated', label: 'projects.sortUpdated' },
  { id: 'name', label: 'projects.sortName' },
];

interface ProjectsToolbarProps {
  t: Translate;
  filter: ProjectFilter;
  onFilter: (next: ProjectFilter) => void;
  onCreate: (name: string) => void;
  /** True while a create or trash request is in flight; disables the Create button. */
  busy?: boolean;
}

/** Controlled by ProjectsScreen; `onCreate` receives the trimmed, non-empty name; the trash button is labelled with the view it opens. */
export function ProjectsToolbar({ t, filter, onFilter, onCreate, busy = false }: ProjectsToolbarProps) {
  const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const name = formField(new FormData(event.currentTarget), 'name').trim();
    if (name !== '') onCreate(name);
  };
  return (
    <div className="projects-toolbar">
      <input type="search" value={filter.query} placeholder={t('projects.search')} aria-label={t('projects.search')} onChange={(e) => { onFilter({ ...filter, query: e.target.value }); }} />
      <select value={filter.sort} aria-label={t('projects.sort')} onChange={(e) => { onFilter({ ...filter, sort: e.target.value === 'name' ? 'name' : 'updated' }); }}>
        {SORTS.map((s) => <option key={s.id} value={s.id}>{t(s.label)}</option>)}
      </select>
      <button type="button" onClick={() => { onFilter({ ...filter, trash: !filter.trash }); }}>
        {t(filter.trash ? 'projects.showActive' : 'projects.showTrash')}
      </button>
      <form className="projects-new" onSubmit={onSubmit}>
        <input name="name" placeholder={t('projects.newName')} aria-label={t('projects.newName')} required />
        <button type="submit" disabled={busy}>{t('projects.create')}</button>
      </form>
    </div>
  );
}
